import { Component, OnInit, signal } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  GoogleAuthProvider,
  RecaptchaVerifier,
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
} from 'firebase/auth';
import { CountryISO, NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { arrayUnion, doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, dataStore, db, functions } from 'src/app/app.component';
import { CustomValidators } from 'angular-custom-validators';
import { httpsCallable } from 'firebase/functions';
import { loginDataInit } from 'src/app/globals';
import { RootUser } from 'src/app/interfaces';
import { MatFormField, MatLabel, MatInput, MatError, MatSuffix } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgxIntlTelInputModule,
    MatError,
    MatIconButton,
    MatSuffix,
    MatTooltip,
    MatIcon,
    MatButton,
    NgClass,
    RouterLink,
  ],
})
export class RegisterComponent implements OnInit {
  signUpType = signal<'phone' | 'email'>('email');
  phoneSignUpStatus = signal<'default' | 'verification'>('default');
  emailForm: UntypedFormGroup = new UntypedFormGroup({
    email: new UntypedFormControl('', [CustomValidators.email, Validators.required]),
    password: new UntypedFormControl('', [Validators.minLength(6), Validators.required]),
    passwordConfirm: new UntypedFormControl('', [Validators.minLength(6), Validators.required]),
  });
  phoneForm: UntypedFormGroup = new UntypedFormGroup({
    phoneNumber: new UntypedFormControl('', [Validators.required]),
  });
  verificationForm: UntypedFormGroup = new UntypedFormGroup({
    code: new UntypedFormControl('', [Validators.required]),
  });

  phoneSubmitted = signal(false);
  awaitingResponse = signal(false);
  showPasswords = signal(false);
  CountryISO = CountryISO;
  windowRef: any;
  verificationCode: string;
  captcha = signal(false);
  captchaIsRendering = signal(false);
  verificationError = false;

  loading = signal(true);

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.phoneForm.controls.phoneNumber.valueChanges.subscribe((value) => {
      this.generateCaptcha(value);
      if (this.phoneForm.controls.phoneNumber.hasError('already-has-org')) {
        this.phoneForm.controls.phoneNumber.setErrors({
          'already-has-org': null,
        });
        this.phoneForm.controls.phoneNumber.updateValueAndValidity();
      }
      if (this.phoneForm.controls.phoneNumber.hasError('user-already-exists')) {
        this.phoneForm.controls.phoneNumber.setErrors({
          'user-already-exists': null,
        });
        this.phoneForm.controls.phoneNumber.updateValueAndValidity();
      }
    });
    this.emailForm.controls.email.valueChanges.subscribe((value) => {
      if (this.emailForm.controls.email.hasError('already-has-org')) {
        this.emailForm.controls.email.setErrors({ 'already-has-org': null });
        this.emailForm.controls.email.updateValueAndValidity();
      }
      if (this.emailForm.controls.email.hasError('user-already-exists')) {
        this.emailForm.controls.email.setErrors({
          'user-already-exists': null,
        });
        this.emailForm.controls.email.updateValueAndValidity();
      }
    });
    this.loading.set(false);
  }

  async signUp(type: string) {
    switch (type) {
      case 'email':
        this.emailSignUp();
        break;
      case 'phone':
        this.phoneSubmitted.set(true);
        this.phoneSignUp();
        break;
    }
  }

  async emailSignUp() {
    if (!this.emailForm.valid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const form = this.emailForm.value;
    this.awaitingResponse.set(true);
    //TODO CHANGE TO ACTUAL ERROR MESSAGE RATHER THAN SNACKBAR
    if (form.password !== form.passwordConfirm) {
      this.awaitingResponse.set(false);
      this.snackBar.open('De wachtwoorden komen niet overeen.', 'X');
      return;
    }
    let haltLogin: boolean = false;
    const callable = httpsCallable(functions, 'accountCheckInfo');
    const callableResult = await callable({
      type: 'email',
      check: 'register',
      orgId: dataStore.selectedOrganisation.id,
      accountData: form.email,
    });
    console.log('test', callableResult);
    switch (callableResult.data['message']) {
      case 'already-has-org':
        this.emailForm.controls.email.setErrors({
          'already-has-org': true,
        });
        haltLogin = true;
        break;
      case 'user-already-exists':
        this.emailForm.controls.email.setErrors({
          'user-already-exists': true,
        });
        haltLogin = true;
        break;
      case 'no-problems':
        break;
      default:
        this.snackBar.open('Er is een onbekende fout opgetreden. Probeer het later opnieuw.', 'X');
        haltLogin = true;
        break;
    }

    if (!haltLogin) {
      try {
        const signUp = await createUserWithEmailAndPassword(auth, form.email, form.password);
        this.awaitingResponse.set(false);
        await setDoc(
          doc(db, `users/${signUp.user.uid}`),
          {
            signInMethod: 'email',
            email: form.email,
            selectedOrgId: dataStore.selectedOrganisation.id,
            organisations: [
              {
                id: dataStore.selectedOrganisation.id,
                name: dataStore.selectedOrganisation.name,
              },
            ],
          },
          { merge: true },
        );
        await loginDataInit(auth.currentUser, this.router, true, this.iconRegistry, this.sanitizer);
      } catch (error) {
        console.error('Error: ', error);
        this.awaitingResponse.set(false);
        switch (error.code) {
          default:
            this.snackBar.open(
              'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
              'X',
            );
            break;
        }
      }
    } else {
      this.awaitingResponse.set(false);
    }
  }

  async phoneSignUp() {
    if (!this.phoneForm.valid) {
      this.phoneForm.markAllAsTouched();
      return;
    }
    this.phoneSignUpStatus.set('verification');
    const form = this.phoneForm.value;
    if (form.phoneNumber && form.phoneNumber.e164Number !== '') {
      this.awaitingResponse.set(true);
      let haltLogin: boolean = false;
      const callable = httpsCallable(functions, 'accountCheckInfo');
      const callableResult = await callable({
        type: 'phone',
        check: 'register',
        orgId: dataStore.selectedOrganisation.id,
        accountData: form.phoneNumber.e164Number,
      });
      switch (callableResult.data['message']) {
        case 'already-has-org':
          this.phoneForm.controls.phoneNumber.setErrors({
            'already-has-org': true,
          });
          haltLogin = true;
          break;
        case 'user-already-exists':
          this.phoneForm.controls.phoneNumber.setErrors({
            'user-already-exists': true,
          });
          haltLogin = true;
        case 'no-problems':
          break;
        default:
          this.snackBar.open(
            'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
            'X',
          );
          haltLogin = true;
          break;
      }
      if (!haltLogin) {
        try {
          const phoneNumberResult = await signInWithPhoneNumber(
            auth,
            this.phoneForm.value.phoneNumber.e164Number,
            this.windowRef.recaptchaVerifier,
          );
          this.awaitingResponse.set(false);
          this.signUpType.set('phone');
          this.phoneSignUpStatus.set('verification');
          this.windowRef.confirmationResult = phoneNumberResult;
          this.snackBar.open(
            'Er is een verificatie code verstuurd naar je telefoon. Dit kan een paar minuten duren.',
            'X',
          );
        } catch (error) {
          console.log('Error: ', error);
          this.awaitingResponse.set(false);
          switch (error.code) {
            default:
              this.snackBar.open(
                'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
                'X',
              );
              break;
          }
        }
      } else {
        this.awaitingResponse.set(false);
      }
    }
  }

  goBackFromVerification() {
    const phoneValue = this.phoneForm.controls.phoneNumber.value;
    const rawNumber = phoneValue?.e164Number || phoneValue;
    this.phoneForm.controls.phoneNumber.setValue(rawNumber);
    this.phoneForm.controls.phoneNumber.setErrors(null);
    this.phoneForm.controls.phoneNumber.markAsUntouched();
    this.phoneSubmitted.set(false);
    this.phoneSignUpStatus.set('default');
  }

  async verifyLoginCode() {
    if (!this.verificationForm.valid) {
      this.verificationForm.markAllAsTouched();
      return;
    }

    if (!this.windowRef.confirmationResult) {
      //TODO CHANGE TO ACTUAL FORMFIELD ERROR
      this.snackBar.open('De code is ongeldig.', 'X');
      return;
    }
    try {
      const verificationResult = await this.windowRef.confirmationResult.confirm(
        this.verificationForm.value.code,
      );
      await setDoc(
        doc(db, `users/${verificationResult.user.uid}`),
        {
          signInMethod: 'phoneNumber',
          phoneNumber: this.phoneForm.value.phoneNumber.e164Number,
          selectedOrgId: dataStore.selectedOrganisation.id,
          organisations: [
            {
              id: dataStore.selectedOrganisation.id,
              name: dataStore.selectedOrganisation.name,
            },
          ],
        },
        { merge: true },
      );
      await loginDataInit(auth.currentUser, this.router, true, this.iconRegistry, this.sanitizer);
    } catch (error) {
      //TODO CHANGE THIS TO ACTUAL FORMFIELD ERROR IN SWITCH CASE
      // this.snackBar.open('De code is ongeldig', 'X');
      console.error('Error: ', error);
      this.awaitingResponse.set(false);
      switch (error.code) {
        default:
          this.snackBar.open(
            'Er is een onbekende fout opgetreden. Probeer het later opnieuw.',
            'X',
          );
          break;
      }
    }
  }

  generateCaptcha(value: any) {
    if (value && !this.captcha() && !this.captchaIsRendering()) {
      this.captchaIsRendering.set(true);
      this.captcha.set(true);
      setTimeout(() => {
        try {
          this.windowRef = window;
          this.windowRef.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
          });
          this.windowRef.recaptchaVerifier.render();
        } catch (error) {
          console.error('reCAPTCHA initialization failed', error);
          this.captchaIsRendering.set(false);
          this.captcha.set(false);
        }
      }, 100);
    }
  }

  async googleLogin() {
    try {
      const googlePopUpResult = await signInWithPopup(auth, new GoogleAuthProvider());
      const userRef = doc(db, `users/${googlePopUpResult.user.uid}`);
      const rootUser = (await getDoc(userRef)).data() as RootUser;

      if (!userRef || !rootUser?.organisations) {
        setDoc(
          userRef,
          {
            organisations: [
              {
                id: dataStore.selectedOrganisation.id,
                name: dataStore.selectedOrganisation.name,
              },
            ],
            selectedOrganisation: dataStore.selectedOrganisation.id,
            signInMethod: 'google',
          },
          { merge: true },
        );
      } else {
        const userUpdateObj = {
          selectedOrgId: dataStore.selectedOrganisation.id,
        } as any;
        if (rootUser && rootUser.organisations) {
          if (!rootUser.organisations.find((x) => x.id === dataStore.selectedOrganisation.id)) {
            userUpdateObj.organisations = arrayUnion({
              id: dataStore.selectedOrganisation.id,
              name: dataStore.selectedOrganisation.name,
            });
          }
        }
        await setDoc(userRef, userUpdateObj, { merge: true });
      }
      await loginDataInit(
        googlePopUpResult.user,
        this.router,
        false,
        this.iconRegistry,
        this.sanitizer,
      );
    } catch (error) {
      console.log(error);
    }
  }
}
